"use client"

import NextLink from "next/link"
import React from "react"
import { Text, Image } from "@/elements"
import { graphql } from "@/gql"
import { ProjectPreviewFragment } from "@/gql/graphql"
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline"
import { PortfolioEditModal, useEditPortfolio } from "../edit/useEditPortfolio"
import { PortfolioUIState } from "../../page"
import { useRouter } from "next-nprogress-bar"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"

graphql(`
  fragment ProjectPreview on CreativeWorkPortfolioProject {
    id
    title
    creatorRolesString
    thumbnailFocalPointX
    thumbnailFocalPointY
    thumbnail645w: thumbnailUrl(width: 645)
    thumbnail1290w: thumbnailUrl(width: 1290)
  }
`)

interface Props {
  project: ProjectPreviewFragment
  handle: string | undefined | null
  imageSize?: "sm" | "md" | "lg"
  priority?: boolean
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
}

export const ProjectPreview: React.FC<Props> = ({
  handle,
  project,
  imageSize = "645",
  priority = false,
  editProfileMethods,
}) => {
  const router = useRouter()

  let srcSetUrls
  switch (imageSize) {
    case "sm":
      srcSetUrls = {
        sm: project?.thumbnail645w ?? "",
        md: project?.thumbnail645w ?? "",
        lg: project.thumbnail1290w ?? "",
        xl: project?.thumbnail1290w ?? "",
      }
      break
    case "md":
      srcSetUrls = {
        sm: project?.thumbnail645w ?? "",
        md: project?.thumbnail645w ?? "",
        lg: project.thumbnail1290w ?? "",
        xl: project?.thumbnail1290w ?? "",
      }
      break
    case "lg":
    default:
      srcSetUrls = {
        sm: project?.thumbnail645w ?? "",
        md: project?.thumbnail645w ?? "",
        lg: project.thumbnail1290w ?? "",
        xl: project?.thumbnail1290w ?? "",
      }
      break
  }

  const xPosition = project.thumbnailFocalPointX || 0.5
  const yPosition = project.thumbnailFocalPointY || 0.5

  const onClickDelete = () => {
    editProfileMethods?.setOpenModal(PortfolioEditModal.DeleteProject)
    editProfileMethods?.setDeleteProjectId(project.id)
  }

  const onClickEdit = () => {
    router.push(`/${handle}/projects/form?projectId=${project.id}&ref=edit-profile`)
  }

  return (
    <NextLink
      href={`/${handle}/projects/${project.id}`}
      onClick={() => {
        ampli.projectView({
          project_id: decodeGraphqlNodeId(project?.id).numericalId || "",
          entry_point: "portfolio",
        })
      }}>
      <div className="group relative w-full overflow-hidden rounded-sm md:rounded-md" style={{ paddingTop: "75%" }}>
        {editProfileMethods?.isEditing && (
          <div className="absolute bottom-4 right-4 z-10 flex flex-row items-center gap-2 rounded-md">
            <div
              className="flex size-[32px] items-center justify-center rounded-md"
              style={{
                background: "rgba(0, 0, 0, 0.35)",
              }}>
              <PencilIcon
                className="size-[22px] stroke-bg-1"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onClickEdit()
                }}
              />
            </div>
            <div
              data-prevent-nprogress={true}
              className="z-10 flex size-[32px] items-center justify-center rounded-md"
              style={{
                background: "rgba(0, 0, 0, 0.35)",
              }}>
              <TrashIcon
                className="size-[22px] stroke-bg-1"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onClickDelete()
                }}
              />
            </div>
          </div>
        )}
        <Image
          srcSetUrls={srcSetUrls}
          alt={project?.title ?? ""}
          className="absolute left-0 top-0 h-full w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.05]"
          fetchPriority={priority ? "high" : "auto"}
          style={{
            objectPosition: `${xPosition * 100}% ${yPosition * 100}%`,
          }}
        />
      </div>
      <div className="mt-2">
        <Text variant="label" maxLines={2}>
          {project.title}
        </Text>
        <Text variant="caption" maxLines={1} className="text-fg-2">
          {project.creatorRolesString}
        </Text>
      </div>
    </NextLink>
  )
}
